import { Modal, SubmitButton } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import { Path } from "history";
import React, { useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { SecondaryButton } from "./SecondaryButton";
import { FormState } from "@/components/Common/MessageModal/MessageModalTypes";

interface ConfirmLeaveModalProps {
    copy?: string;
    dirty?: boolean;
    submitCount?: number;
    isFormTouched?: boolean;
    disableRouterPrompt?: boolean;
    manualShowPrompt?: boolean;
    handleConfirm?: () => void;
    handleCancel?: () => void;
}

/**
 * The component relies on one of the following:
 * 1) a combination of `dirty` and `submitCount` properties is used with Formik
 * 2) `isFormTouched` property if used outside of Formik but with react-router Prompt
 * 3) full manual usage without formik/react router Prompt, using `manualShowPrompt`, `disableRouterPrompt`, `handleConfirm`, `handleCancel` params
 *
 * @param copy modal copy to display instead of the default one
 *
 * 1)
 * @param dirty Formik property to use modal inside the <XolaFormik> component, false by default
 * @param submitCount Formik property to use modal inside the <XolaFormik> component, 0 by default
 *
 * 2)
 * @param isFormTouched property to use modal without the <XolaFormik>, relying on react router Prompt only, false by default
 *
 * 3)
 * @param disableRouterPrompt property to disable react router Prompt
 * @param manualShowPrompt property to manually handle visibility of the modal
 * @param handleConfirm confirmation handler for cases when react router Prompt is disabled
 * @param handleCancel cancelation handler for cases when react router Prompt is disabled
 */
export const ConfirmLeaveModal = ({
    copy,
    dirty = false,
    submitCount = 0,
    isFormTouched = false,
    disableRouterPrompt = false,
    manualShowPrompt = false,
    handleConfirm,
    handleCancel,
}: ConfirmLeaveModalProps) => {
    const history = useHistory();
    const [isOpen, setIsOpen] = useState(false);
    const [nextLocation, setNextLocation] = useState<string>();
    const [confirmed, setConfirmed] = useState(false);

    //
    // READ ME: The code below will help you find out what value in the form is causing this modal to show up
    //
    const context = useFormikContext<FormState>();
    const hasGuestsChanged = context?.values && ("guests" in context.values || "formResponse" in context.values);

    const handleClose = () => {
        setIsOpen(false);
        handleCancel?.();
    };

    useEffect(() => {
        if (disableRouterPrompt) {
            setIsOpen(manualShowPrompt);
        }
    }, [manualShowPrompt, disableRouterPrompt]);

    useEffect(() => {
        if (!disableRouterPrompt && confirmed && nextLocation) {
            history.push(nextLocation);
        }
    }, [confirmed, nextLocation, history, disableRouterPrompt]);

    const handleDiscard = () => {
        setIsOpen(false);
        setConfirmed(true);
        handleConfirm?.();
    };

    const handleBlockedNavigation = (location: Path) => {
        if (!confirmed) {
            setIsOpen(true);
            setNextLocation(location.pathname);
            return false;
        }

        return true;
    };

    const message =
        copy ?? "You have made changes that have not been saved yet. Are you sure you want to discard changes?";

    return (
        <>
            {!disableRouterPrompt && (
                <Prompt
                    when={(dirty && !hasGuestsChanged && submitCount === 0) || isFormTouched}
                    message={handleBlockedNavigation}
                />
            )}

            <Modal isOpen={isOpen} onClose={handleClose}>
                <Modal.Header>Unsaved Changes</Modal.Header>
                <Modal.Body>
                    <div className="space-y-4" data-testid="confirm-leave-modal-message">
                        <p className="p1">{message}</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                    <SubmitButton color="danger" onClick={handleDiscard}>
                        Discard
                    </SubmitButton>
                </Modal.Footer>
            </Modal>
        </>
    );
};
